import React from "react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { ChevronRight, Leaf, Users, Store, Recycle } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useContent } from "../hooks/useContent";

const paragraphKeys = ["st.para1", "st.para2", "st.para3", "st.para4", "st.para5"];
const values = [
  { icon: Store, titleKey: "st.supportLocal", textKey: "st.valSupportLocal" },
  { icon: Recycle, titleKey: "st.wasteLess", textKey: "st.valWasteLess" },
  { icon: Users, titleKey: "st.communityFirst", textKey: "st.valCommunity" },
  { icon: Leaf, titleKey: "st.sustainable", textKey: "st.valSustainable" },
];

const communityImages = [
  { src: "https://images.unsplash.com/photo-1765480953875-a7338f896e91?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", alt: "Baskets of fresh produce at a local market stall" },
  { src: "https://images.unsplash.com/photo-1775677073681-194049b1a7d3?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", alt: "Colourful fresh vegetables on display at a community market" },
];

const OurStory = () => {
  const { t } = useLang();
  const cms = useContent("story") || {};
  const heroImage = cms.image || communityImages[0].src;
  const cmsParas = String(cms.body || "").split("\n\n").map((p) => p.trim()).filter(Boolean);
  const cmsValues = cms.values?.length ? cms.values : null;
  return (
  <div className="space-y-8 lp-fade-up max-w-4xl">
    <div className="relative rounded-3xl overflow-hidden text-white p-8 min-h-[200px] flex flex-col justify-end" data-testid="our-story-hero">
      <img src={heroImage} alt={communityImages[0].alt} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0e6b3e]/95 via-[#0e6b3e]/70 to-[#0e6b3e]/30" />
      <div className="relative">
        <h1 className="text-3xl md:text-4xl font-extrabold mb-2">{cms.headline || t("st.ourStory")}</h1>
        <p className="text-white/90">{cms.tagline || t("st.saveMoreWasteLess")}</p>
      </div>
    </div>

    <div className="space-y-5">
      <p className="text-lg text-muted-foreground" data-testid="story-mission-text">{cms.mission || t("st.mission")}</p>
      <p className="text-2xl font-extrabold italic text-foreground">
        Save <span className="text-[#16a34a]">More</span>. Waste <span className="text-[#16a34a]">Less</span>. Support <span className="text-[#16a34a]">Local</span>.
      </p>
      {cms.bodyHtml ? (
        <div className="lp-rich text-[16px] text-muted-foreground" data-testid="story-rich-body"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cms.bodyHtml) }} />
      ) : (cmsParas.length ? cmsParas : paragraphKeys.map((p) => t(p))).map((p, i) => (
        <p key={i} className="text-[16px] leading-relaxed text-muted-foreground" data-testid={`story-para-${i}`}>{p}</p>
      ))}

      <div className="grid sm:grid-cols-2 gap-4 pt-2" data-testid="our-story-gallery">
        {communityImages.map((img) => (
          <img key={img.src} src={img.src} alt={img.alt} className="w-full h-48 object-cover rounded-2xl border border-border" loading="lazy" />
        ))}
      </div>

      <p className="text-xl font-bold text-foreground pt-2">{t("st.moreThanADirectory")}</p>
      <p className="text-[16px] leading-relaxed text-muted-foreground">
        {t("st.lytepressIsAGrowing")}
      </p>
      <p className="text-[16px] leading-relaxed text-muted-foreground">
        {t("st.asWeExpandThroughout")}
      </p>
      <p className="text-[16px] leading-relaxed text-muted-foreground">
        {t("st.lookingAheadOurGoal")}
      </p>
      <p className="text-[16px] leading-relaxed text-muted-foreground">
        {t("st.thinkOfItThis")}
      </p>
      <p className="text-[16px] leading-relaxed text-muted-foreground">
        {t("st.whetherYouReA")}
      </p>
    </div>

    <div className="grid sm:grid-cols-2 gap-4" data-testid="story-values">
      {(cmsValues || values).map((v, i) => {
        const Icon = v.icon || values[i % values.length].icon;
        return (
          <div key={v.titleKey || v.title || i} className="bg-card rounded-2xl p-6 border border-border" data-testid={`story-value-card-${i}`}>
            <span className="h-12 w-12 rounded-xl bg-[#e7f6ec] flex items-center justify-center mb-4"><Icon size={24} className="text-[#16a34a]" /></span>
            <h3 className="font-bold text-lg text-foreground mb-1">{v.title || t(v.titleKey)}</h3>
            <p className="text-sm text-muted-foreground">{v.body || t(v.textKey)}</p>
          </div>
        );
      })}
    </div>

    <div className="flex flex-wrap gap-3">
      <Link to="/browse" className="inline-flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white font-semibold px-7 py-3.5 rounded-xl transition-colors">{t("st.exploreDeals")} <ChevronRight size={18} /></Link>
      <Link to="/contact-us" className="inline-flex items-center gap-2 bg-secondary hover:bg-[#e7f6ec] text-foreground font-semibold px-7 py-3.5 rounded-xl transition-colors">{t("st.contactUs")}</Link>
    </div>
  </div>
);
}


export default OurStory;
